"use client";

import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {
    SENSITIVE_DATA_OBFUSCATION_ATTRIBUTE,
    SENSITIVE_DATA_OBFUSCATION_STORAGE_KEY,
} from "~/components/obfuscation/storage";

type ObfuscationContextValue = {
    isObfuscationEnabled: boolean;
    setObfuscationEnabled: (enabled: boolean) => void;
};

const ObfuscationContext = createContext<ObfuscationContextValue | undefined>(undefined);

const updateDocumentAttribute = (enabled: boolean) => {
    if (enabled) {
        document.documentElement.setAttribute(SENSITIVE_DATA_OBFUSCATION_ATTRIBUTE, "true");
    } else {
        document.documentElement.removeAttribute(SENSITIVE_DATA_OBFUSCATION_ATTRIBUTE);
    }
};

export function ObfuscationProvider({children}: {children: React.ReactNode}) {
    const [isObfuscationEnabled, setIsObfuscationEnabled] = useState(false);

    useEffect(() => {
        let storedValue = false;

        try {
            storedValue = window.localStorage.getItem(SENSITIVE_DATA_OBFUSCATION_STORAGE_KEY) === "true";
        } catch {
            storedValue = false;
        }

        setIsObfuscationEnabled(storedValue);
        updateDocumentAttribute(storedValue);

        const handleStorage = (event: StorageEvent) => {
            if (event.key !== SENSITIVE_DATA_OBFUSCATION_STORAGE_KEY && event.key !== null) return;

            const enabled = event.newValue === "true";
            setIsObfuscationEnabled(enabled);
            updateDocumentAttribute(enabled);
        };

        window.addEventListener("storage", handleStorage);
        return () => window.removeEventListener("storage", handleStorage);
    }, []);

    const setObfuscationEnabled = useCallback((enabled: boolean) => {
        setIsObfuscationEnabled(enabled);
        updateDocumentAttribute(enabled);

        try {
            if (enabled) {
                window.localStorage.setItem(SENSITIVE_DATA_OBFUSCATION_STORAGE_KEY, "true");
            } else {
                window.localStorage.removeItem(SENSITIVE_DATA_OBFUSCATION_STORAGE_KEY);
            }
        } catch {
            // The visual setting still applies for the current page when storage is unavailable.
        }
    }, []);

    const value = useMemo(
        () => ({isObfuscationEnabled, setObfuscationEnabled}),
        [isObfuscationEnabled, setObfuscationEnabled],
    );

    return (
        <ObfuscationContext.Provider value={value}>
            {children}
        </ObfuscationContext.Provider>
    );
}

export function useObfuscation() {
    const context = useContext(ObfuscationContext);

    if (!context) {
        throw new Error("useObfuscation must be used within an ObfuscationProvider.");
    }

    return context;
}
